import { useEffect, useState } from "react";
import { BookOpen, Download, Paperclip, Plus, Search, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { useEscape } from "@/lib/useEscape";
import { Pagination, usePagination } from "@/components/Pagination";
import JournalForm from "@/pages/JournalForm";

const fmtDate = (d) => (d ? d.split("-").reverse().join("-") : "—");

export default function Journals() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(null);
  useEscape(() => setSelected(null), Boolean(selected));
  const [message, setMessage] = useState(null);
  const load = () => api("/journals").then(setRows).catch((e) => setMessage({ text: e.message, type: "error" }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 7000);
    return () => clearTimeout(t);
  }, [message]);
  const filtered = rows.filter((r) => [r.number, r.date, r.description, r.reference, r.tag, r.status].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(filtered, 25, search);
  if (creating || editing) return <JournalForm initial={editing} onCancel={() => { setCreating(false); setEditing(null); }} onSaved={(r) => { const edited = Boolean(editing); setCreating(false); setEditing(null); setMessage({ text: edited ? `${r.number} berhasil diubah — jurnal diperbarui` : `${r.number} berhasil diposting — total ${money(r.total_debit)}`, type: "success" }); load(); }} />;
  const downloadAttachment = async (j) => {
    const doc = await api(`/journals/${j.id}/attachment`);
    const a = document.createElement("a");
    a.href = `data:${doc.content_type};base64,${doc.data}`;
    a.download = doc.filename;
    a.click();
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div><p className="eyebrow">TRANSAKSI</p><h1>Jurnal Umum</h1></div>
        <button data-testid="journal-create-button" className="primary-btn" onClick={() => setCreating(true)}><Plus size={16} /> Buat Jurnal Umum</button>
      </div>
      {message && <div data-testid={message.type === "error" ? "journal-error-message" : "journal-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="workflow-toolbar surface">
        <div className="workflow-search"><Search size={16} /><input data-testid="journal-search-input" placeholder="Cari jurnal..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <span className="record-count">{filtered.length} data</span>
      </div>
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="journal-table">
          <thead><tr><th>NOMOR</th><th>TANGGAL</th><th>DESKRIPSI</th><th>REFERENSI</th><th>TAG</th><th>STATUS</th><th className="right">TOTAL (IDR)</th></tr></thead>
          <tbody>
            {pager.pageItems.map((r) => (
              <tr key={r.id} onClick={() => setSelected(r)} data-testid={`journal-row-${r.number}`}>
                <td className="primary-cell">{r.number}</td>
                <td>{fmtDate(r.date)}</td>
                <td>{r.description || r.memo || "—"}</td>
                <td>{r.reference || "—"}</td>
                <td>{r.tag ? <span className="exp-tag">{r.tag}</span> : "—"}</td>
                <td><span className={`status ${(r.status || "").toLowerCase()}`}>{r.status === "POSTED" ? "Posted" : r.status === "VOID" ? "Void" : r.status}</span></td>
                <td className="right mono">{money(r.total_debit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="journal-empty-state"><BookOpen size={25} /><strong>Belum ada jurnal</strong><span>Buat jurnal umum pertama Anda.</span></div>}
        <Pagination pager={pager} testid="journal-pagination" />
      </div>
      {selected && (
        <>
          <div className="drawer-backdrop" onClick={() => setSelected(null)} />
          <div className="detail-drawer v2" data-testid="journal-detail-drawer">
            <div className="drawer-hero">
              <div className="drawer-hero-top">
                <div>
                  <p className="eyebrow">JURNAL UMUM</p>
                  <h2>{selected.number}</h2>
                  {(selected.description || selected.memo) && <p className="drawer-hero-party">{selected.description || selected.memo}</p>}
                </div>
                <button data-testid="journal-close-detail" className="icon-btn light" onClick={() => setSelected(null)}><X size={18} /></button>
              </div>
              <div className="drawer-hero-meta">
                <span className={`status ${(selected.status || "").toLowerCase()}`}>{selected.status === "POSTED" ? "Posted" : selected.status === "VOID" ? "Void" : selected.status}</span>
                <span>{fmtDate(selected.date)}</span>
              </div>
              <div className="drawer-hero-stats two">
                <div><small>Total Debit</small><b>{money(selected.total_debit)}</b></div>
                <div><small>Total Kredit</small><b>{money(selected.total_credit)}</b></div>
              </div>
            </div>
            <div className="drawer-body">
              {selected.status !== "VOID" && selected.reference && (
                <p className="muted ju-auto-note" data-testid="journal-auto-note">Jurnal ini dibuat otomatis dari <b>{selected.reference}</b> — ubah atau hapus lewat dokumen sumbernya.</p>
              )}
              {(selected.reference || selected.tag || selected.memo || selected.description) && (
                <>
                  <div className="drawer-section-title">Informasi Jurnal</div>
                  <div className="detail-fields v2">
                    {selected.reference && <div><small>Referensi</small><strong>{selected.reference}</strong></div>}
                    {selected.tag && <div><small>Tag</small><strong>{selected.tag}</strong></div>}
                    {(selected.memo || selected.description) && <div><small>Memo</small><strong>{selected.memo || selected.description}</strong></div>}
                  </div>
                </>
              )}
              <div className="doc-lines v2">
                <div className="drawer-section-title">Baris Jurnal</div>
                <table className="doc-line-table v2">
                  <thead><tr><th>AKUN</th><th>DESKRIPSI</th><th className="right">DEBIT</th><th className="right">KREDIT</th></tr></thead>
                  <tbody>
                    {(selected.lines || []).map((l, i) => (
                      <tr key={i}><td className="mono">{l.account_code}</td><td>{l.description || "—"}</td><td className="right mono">{l.debit ? money(l.debit) : ""}</td><td className="right mono">{l.credit ? money(l.credit) : ""}</td></tr>
                    ))}
                  </tbody>
                </table>
                <div className="doc-totals v2">
                  <div className="grand"><span>Total Debit</span><b>{money(selected.total_debit)}</b></div>
                  <div className="grand"><span>Total Kredit</span><b>{money(selected.total_credit)}</b></div>
                </div>
              </div>
              {selected.has_attachment && (
                <div className="journal-lines">
                  <div className="drawer-section-title">Lampiran</div>
                  <div><span><Paperclip size={13} /> {selected.attachment_name}</span><button data-testid="journal-download-attachment" className="text-btn" onClick={() => downloadAttachment(selected)}><Download size={13} /> Unduh</button></div>
                </div>
              )}
              {selected.status !== "VOID" && !selected.reference && (
                <>
                  <div className="drawer-section-title">Tindakan</div>
                  <div className="drawer-actions v2">
                    <button data-testid="journal-edit-button" className="secondary-btn" onClick={() => { setEditing(selected); setSelected(null); }}>Ubah</button>
                    <button data-testid="journal-delete-button" className="danger-btn" onClick={() => setDeleting(selected)}>Hapus</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
      {deleting && (
        <div className="modal-overlay" data-testid="journal-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
            <div className="section-head">
              <div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Jurnal?</h2></div>
              <button className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button>
            </div>
            <p className="muted delete-warning">"{deleting.number}" akan dibatalkan (void) dan tidak lagi dihitung dalam laporan. Jurnal otomatis dari transaksi lain harus dihapus lewat dokumen sumbernya.</p>
            <div className="form-actions">
              <button data-testid="journal-delete-confirm" className="danger-btn" onClick={async () => {
                try {
                  await api(`/journals/${deleting.id}`, { method: "DELETE" });
                  setSelected(null);
                  setMessage({ text: `${deleting.number} dibatalkan (void)`, type: "success" });
                  load();
                } catch (e) { setMessage({ text: e.message, type: "error" }); }
                setDeleting(null);
              }}>Ya, Hapus</button>
              <button data-testid="journal-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
